// src/services/readerSignals.js
// The reader-signal rail: "where readers land" position aggregates and the
// per-module live-discussion demand probe.
//
// Design constraints this file exists to honour:
//   • Positions stay device-local truth (usePositions). This rail only
//     mirrors each committed choice into an anonymous, rules-guarded
//     Firestore counter so readers can see how OTHER readers landed —
//     zero user-generated content, zero moderation surface.
//   • The write session is an ANONYMOUS user on the app's own project
//     (ddd-profound-philosophy) via a separately named Firebase app.
//     AuthContext folds a default-app user into the signed-in UI state
//     (`ownUser || hubUser`), so the anon session must never live on the
//     default app or every visitor would appear signed in.
//   • Aggregate reads need no auth at all (rules: public read), so
//     percentages render without minting an anonymous user. A user is
//     minted lazily, on the first committed signal from this device.
//   • Everything fails soft. If the network, auth or rules say no, the
//     reader's local experience is untouched and the UI simply shows no
//     aggregate. The last successfully signalled choice is mirrored in
//     localStorage so a failed write is retried the next time the fork
//     is on screen (reconciliation), not lost.
//
// Honest semantics, stated where Nigel can find them: one voice per
// device, not per person (an anonymous uid is per browser profile, and
// Safari's storage eviction can re-mint one). Fine for percentages;
// never to be reused for anything that needs identity.
//
// Australian English throughout.

import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth, signInAnonymously, onAuthStateChanged, connectAuthEmulator,
} from 'firebase/auth';
import {
  getFirestore, initializeFirestore, doc, getDoc, writeBatch,
  serverTimestamp, increment, connectFirestoreEmulator,
} from 'firebase/firestore';

// Dev-only: point this rail at the local emulators (the pair that
// scripts/test-reader-signal-rules.mjs uses) so the full UI loop can be
// exercised without touching production counters. Never set in a
// production build's env.
const USE_EMULATOR = import.meta.env.VITE_SIGNALS_EMULATOR === 'true';

const SIGNALS_APP_NAME = 'reader-signals';
const SIGNALLED_KEY = 'profoundPhilosophy_signalledPositions';
const DEMAND_KEY = 'profoundPhilosophy_signalledDemand';
export const SIGNALS_EVENT = 'reader-signals-updated';

// ── The isolated Firebase app ───────────────────────────────────────────────

let signalsApp = null;
function getSignalsApp() {
  if (signalsApp) return signalsApp;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
  if (!projectId || !apiKey) return null;
  const existing = getApps().find((a) => a.name === SIGNALS_APP_NAME);
  if (existing) {
    signalsApp = existing;
  } else {
    signalsApp = initializeApp({
      apiKey,
      projectId,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
    }, SIGNALS_APP_NAME);
    // WebKit (Safari/iOS and WKWebView panes) blocks the streaming
    // WebChannel, stalling reads/writes ~60s. Force long polling — but only
    // on fresh creation: initializeFirestore throws once an instance exists
    // for the app. Same guard as mindease firebase.js on its default app;
    // a named app needs its own.
    initializeFirestore(signalsApp, { experimentalForceLongPolling: true });
    // Emulator hookup rides the fresh-creation branch too: connecting an
    // already-started instance a second time (HMR re-evaluation) throws.
    if (USE_EMULATOR) {
      connectAuthEmulator(getAuth(signalsApp), 'http://127.0.0.1:9099', { disableWarnings: true });
      connectFirestoreEmulator(getFirestore(signalsApp), '127.0.0.1', 8080);
    }
  }
  return signalsApp;
}

function getSignalsDb() {
  const app = getSignalsApp();
  return app ? getFirestore(app) : null;
}

/**
 * Resolve the anonymous signal session, minting one on first use. The
 * named app persists its own user, so a device keeps one uid across
 * visits. Returns the uid or null (fail soft).
 */
async function ensureSignalSession() {
  const app = getSignalsApp();
  if (!app) return null;
  const auth = getAuth(app);
  // Wait for persistence to restore before deciding to mint a new user.
  await new Promise((resolve) => {
    const unsub = onAuthStateChanged(auth, () => { unsub(); resolve(); }, () => { unsub(); resolve(); });
  });
  if (auth.currentUser) return auth.currentUser.uid;
  try {
    const cred = await signInAnonymously(auth);
    return cred.user.uid;
  } catch (err) {
    console.warn('[readerSignals] anonymous session unavailable:', err?.code || err);
    return null;
  }
}

// ── localStorage mirrors (last choice this device successfully counted) ─────

function readMirror(key) {
  try {
    return JSON.parse(localStorage.getItem(key)) || {};
  } catch {
    return {};
  }
}

function writeMirror(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* private mode — reconciliation just retries more often */ }
}

const announce = () => window.dispatchEvent(new Event(SIGNALS_EVENT));

/**
 * The device's marker doc on the server, for when the localStorage mirror
 * has no entry (cleared storage, new browser). Without this, a client
 * whose mirror is gone would claim "first vote" against a marker that
 * already exists and be denied by the rules forever.
 */
async function readOwnMarker(family, groupId, uid) {
  const db = getSignalsDb();
  if (!db || !uid) return null;
  try {
    const snap = await getDoc(doc(db, family, groupId, 'votes', uid));
    return snap.exists() ? snap.data() : null;
  } catch (err) {
    console.warn('[readerSignals] marker read failed:', err?.code || err);
    return null;
  }
}

// ── Position aggregates ─────────────────────────────────────────────────────

/** The counts doc for one fork: { counts: {optionId: n}, total: n } or null. */
export async function fetchPositionAggregate(pointId) {
  const db = getSignalsDb();
  if (!db) return null;
  try {
    const snap = await getDoc(doc(db, 'positionAggregates', pointId));
    return snap.exists() ? snap.data() : { counts: {}, total: 0 };
  } catch (err) {
    console.warn('[readerSignals] aggregate read failed:', err?.code || err);
    return null;
  }
}

// One in-flight sync per fork so a quick double-tap can't race itself.
const inFlight = {};

/**
 * Mirror this device's committed choice into the shared counter. No-op if
 * the choice is already counted. Returns true when the counter moved.
 */
export async function syncPositionSignal(pointId, choice) {
  if (!pointId || !choice) return false;
  if (inFlight[pointId]) return inFlight[pointId];
  inFlight[pointId] = (async () => {
    try {
      const mirror = readMirror(SIGNALLED_KEY);
      let prev = mirror[pointId] || null;
      if (prev === choice) return false;
      const uid = await ensureSignalSession();
      const db = getSignalsDb();
      if (!uid || !db) return false;

      // Mirror has no entry for this fork: trust the server marker, not
      // the assumption that this is a first vote.
      if (!Object.prototype.hasOwnProperty.call(mirror, pointId)) {
        const marker = await readOwnMarker('positionVotes', pointId, uid);
        prev = marker?.choice || null;
        if (prev === choice) {
          writeMirror(SIGNALLED_KEY, { ...readMirror(SIGNALLED_KEY), [pointId]: choice });
          return false;
        }
      }

      const batch = writeBatch(db);
      batch.set(doc(db, 'positionVotes', pointId, 'votes', uid), {
        choice,
        at: serverTimestamp(),
      });
      const countsPatch = { [choice]: increment(1) };
      if (prev) countsPatch[prev] = increment(-1);
      const aggregatePatch = { counts: countsPatch };
      // Total only grows on this device's first voice at the fork; a
      // changed mind shifts counts and leaves the total alone.
      if (!prev) aggregatePatch.total = increment(1);
      batch.set(doc(db, 'positionAggregates', pointId), aggregatePatch, { merge: true });
      await batch.commit();

      writeMirror(SIGNALLED_KEY, { ...readMirror(SIGNALLED_KEY), [pointId]: choice });
      announce();
      return true;
    } catch (err) {
      // Rules not yet deployed, offline, or a genuine denial: the local
      // position stands, and reconciliation retries on the next view.
      console.warn('[readerSignals] position sync failed:', err?.code || err);
      return false;
    }
  })();
  // Cleanup must chain on the STORED promise, not sit in a finally inside
  // the async body: the no-op path returns with no await, so an inner
  // finally would delete the slot before the assignment re-stored it —
  // wedging the fork with a stale resolved promise for the whole page load.
  inFlight[pointId] = inFlight[pointId].finally(() => { delete inFlight[pointId]; });
  return inFlight[pointId];
}

/** Whether this device's local choice still awaits counting. */
export function positionSignalPending(pointId, choice) {
  return !!choice && readMirror(SIGNALLED_KEY)[pointId] !== choice;
}

// ── Discussion demand ───────────────────────────────────────────────────────

/** How many devices have a hand up for a live discussion on this module. */
export async function fetchDiscussionDemand(moduleId) {
  const db = getSignalsDb();
  if (!db) return null;
  try {
    const snap = await getDoc(doc(db, 'discussionDemand', moduleId));
    return snap.exists() ? (snap.data().count || 0) : 0;
  } catch (err) {
    console.warn('[readerSignals] demand read failed:', err?.code || err);
    return null;
  }
}

/** Whether this device's hand is up for the module. */
export function discussionDemandRaised(moduleId) {
  return readMirror(DEMAND_KEY)[moduleId] === true;
}

/**
 * Raise or withdraw this device's hand. Returns the new local state
 * (true = hand up) or null on failure.
 */
export async function toggleDiscussionDemand(moduleId) {
  let raised = discussionDemandRaised(moduleId);
  try {
    const uid = await ensureSignalSession();
    const db = getSignalsDb();
    if (!uid || !db) return null;

    // No mirror entry: trust the server marker before deciding which way
    // this toggle goes, or a cleared mirror deadlocks against the rules.
    if (!Object.prototype.hasOwnProperty.call(readMirror(DEMAND_KEY), moduleId)) {
      const marker = await readOwnMarker('discussionDemandVotes', moduleId, uid);
      if (marker) raised = marker.interested === true;
    }
    const next = !raised;

    const batch = writeBatch(db);
    batch.set(doc(db, 'discussionDemandVotes', moduleId, 'votes', uid), {
      interested: next,
      at: serverTimestamp(),
    });
    batch.set(doc(db, 'discussionDemand', moduleId), {
      count: increment(next ? 1 : -1),
    }, { merge: true });
    await batch.commit();

    writeMirror(DEMAND_KEY, { ...readMirror(DEMAND_KEY), [moduleId]: next });
    announce();
    return next;
  } catch (err) {
    console.warn('[readerSignals] demand toggle failed:', err?.code || err);
    return null;
  }
}
